/**
 * Storage слой трекера.
 *
 * Visitor ID и Session ID хранятся в HttpOnly cookies на сервере (insecure-free).
 * JS НЕ читает эти cookies (HttpOnly), поэтому identity получаем через GET /api/log/ping.
 *
 * Локально кэшируем identity в memory + sessionStorage для:
 *   - быстрого доступа на чекауте (не надо лишний раз дёргать /ping)
 *   - переживания страничных навигаций (Next.js client-side routing)
 *
 * Также храним first-touch snapshot в localStorage — фронту нужно знать
 * «откуда клиент пришёл изначально» для UI («Спасибо, вы пришли с ВК»).
 */

import type { TrackerIdentity, AttributionSnapshot } from './types';

const SS_KEY_IDENTITY = 'az_tracker_identity';
const LS_KEY_FIRST_TOUCH = 'az_tracker_first_touch';
const LS_KEY_LAST_TOUCH = 'az_tracker_last_touch';

// Memory cache — самый быстрый путь, переживает SPA-навигации, не переживает full reload
let memoryIdentity: TrackerIdentity = {
  visitorId: null,
  sessionId: null,
  fingerprint: null,
};

// ─── Identity ────────────────────────────────────────────────────────────────

export function getIdentity(): TrackerIdentity {
  if (memoryIdentity.visitorId) return memoryIdentity;

  if (typeof window === 'undefined') return memoryIdentity;

  try {
    const raw = sessionStorage.getItem(SS_KEY_IDENTITY);
    if (raw) {
      const parsed = JSON.parse(raw) as TrackerIdentity;
      memoryIdentity = parsed;
      return parsed;
    }
  } catch {
    // sessionStorage недоступна (private mode на iOS Safari иногда)
  }

  return memoryIdentity;
}

export function setIdentity(identity: Partial<TrackerIdentity>): void {
  memoryIdentity = { ...memoryIdentity, ...identity };

  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(SS_KEY_IDENTITY, JSON.stringify(memoryIdentity));
  } catch {
    // ignore
  }
}

// ─── First-touch snapshot (для UI и для journey-display) ─────────────────────

export function getFirstTouch(): AttributionSnapshot | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LS_KEY_FIRST_TOUCH);
    return raw ? (JSON.parse(raw) as AttributionSnapshot) : null;
  } catch {
    return null;
  }
}

export function setFirstTouchIfAbsent(snapshot: AttributionSnapshot): void {
  if (typeof window === 'undefined') return;
  try {
    if (!localStorage.getItem(LS_KEY_FIRST_TOUCH)) {
      localStorage.setItem(LS_KEY_FIRST_TOUCH, JSON.stringify(snapshot));
    }
  } catch {
    // ignore
  }
}

// ─── Last-touch snapshot (обновляется каждый pageview) ──────────────────────

export function getLastTouch(): AttributionSnapshot | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LS_KEY_LAST_TOUCH);
    return raw ? (JSON.parse(raw) as AttributionSnapshot) : null;
  } catch {
    return null;
  }
}

export function setLastTouch(snapshot: AttributionSnapshot): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LS_KEY_LAST_TOUCH, JSON.stringify(snapshot));
  } catch {
    // ignore
  }
}

// ─── Утилиты ─────────────────────────────────────────────────────────────────

/** Используется только для отладки / reset в dev. */
export function clearTrackerStorage(): void {
  memoryIdentity = { visitorId: null, sessionId: null, fingerprint: null };
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(SS_KEY_IDENTITY);
    localStorage.removeItem(LS_KEY_FIRST_TOUCH);
    localStorage.removeItem(LS_KEY_LAST_TOUCH);
  } catch {
    // ignore
  }
}
